"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _ItemContent = _interopRequireDefault(require("./ItemContent"));
var _ExpandCollapse = _interopRequireDefault(require("../ExpandCollapse"));
var _utils = require("../utils");
var _ThemeProvider = require("../ThemeProvider");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 Expandable content areas for use within `SideNav`.

 This component can only be accessed as a name-spaced component: `SideNav.ItemsGroup`.

 `SideNav.ItemsGroup` contains a label and a list of items. Items can link to in-page elements or route to other pages.

 ## Usage Criteria
 - Use `SideNav.ItemsGroup` with large pages that have multiple sections
 */const ItemsGroup = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    children,
    label,
    openGroups,
    groupId,
    isActive = false,
    variant,
    tokens,
    itemTokens,
    onToggle
  } = _ref;
  // A SideNav control uses the same style and theme as SideNavItem, with a 'parent' variant,
  // plus control-specific tokens from the SideNavItemsGroup theme (e.g. open/close icon, etc).
  const getAppearance = appearance => ({
    ...variant,
    ...appearance,
    active: isActive
  });
  const getItemAppearance = appearance => ({
    ...getAppearance(appearance),
    type: 'parent'
  });
  const getGroupTokens = (0, _ThemeProvider.useThemeTokensCallback)('SideNavItemsGroup', tokens, variant);
  const getPanelTokens = appearance => {
    const {
      panelBorderColor,
      ...itemsGroupTokens
    } = getGroupTokens(getAppearance(appearance));
    const groupTokens = {
      ...itemsGroupTokens,
      borderWidth: 0,
      marginBottom: 0
    };
    return (0, _utils.selectTokens)('ExpandCollapsePanel', groupTokens);
  };
  const getItemTokens = (0, _ThemeProvider.useThemeTokensCallback)('SideNavItem', itemTokens, variant);
  const getControlTokens = appearance => (0, _utils.selectTokens)('ExpandCollapseControl', {
    ...getItemTokens(getItemAppearance(appearance)),
    // main style from SideNavItem
    ...getGroupTokens(getAppearance(appearance)) // control-specific tokens like icon etc
  });
  const controlContent = controlState => {
    const currentItemTokens = getItemTokens(getItemAppearance(controlState));
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_ItemContent.default, {
      tokens: currentItemTokens,
      children: label
    });
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_ExpandCollapse.default.Panel, {
    ref: ref,
    openIds: openGroups,
    panelId: groupId,
    onToggle: onToggle,
    tokens: getPanelTokens,
    controlTokens: getControlTokens,
    control: controlContent,
    accessibilityState: {
      active: isActive
    } // ExpandCollapse.Panel handles expanded state
    ,
    children: children
  });
});
ItemsGroup.displayName = 'ItemsGroup';
ItemsGroup.propTypes = {
  /**
   * Specifies the links contained within. It may only contain `SideNav.Item`.
   */
  children: (0, _utils.componentPropType)('Item'),
  /**
   * Text content of the group's parent `SideNav.Item`.
   */
  label: _propTypes.default.string.isRequired,
  /**
   * @ignore
   * Set internally in `SideNav` render function - used to keep track of expanded items groups.
   */
  groupId: _propTypes.default.string,
  /**
   * @ignore
   * Set internally in `SideNav` render function. Called when the group is expanded/collapsed.
   */
  onToggle: _propTypes.default.func,
  /**
   * @ignore
   * Set internally in `SideNav` render function. Contains `groupId`s of currently open groups.
   */
  openGroups: _propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number])),
  /**
   * @ignore
   * Set internally in `SideNav` render function. Used to mark group containing the currently active `SideNav.Item`.
   */
  isActive: _propTypes.default.bool,
  variant: _utils.variantProp.propType,
  tokens: (0, _utils.getTokensPropType)('SideNavItemsGroup'),
  /**
   * Custom tokens for `SideNav.Item`
   */
  itemTokens: (0, _utils.getTokensPropType)('SideNavItem')
};
var _default = exports.default = ItemsGroup;